import React, { useState, useEffect, useContext, useRef } from 'react';
import { getGroupChat, getGroups, getUser, getUserChat } from '../services/adminServices';
import { AppContext } from '../context/AppContext';
import moment from 'moment';
import { toast } from 'react-toastify';
import FileMessage from './FileMessage';
import { copyToClipboard } from '../helper/helper';
import SearchableDropdown from './SearchableDropdown ';

export const Chat = () => {
  const { login } = useContext(AppContext);
  const [users, setUsers] = useState([]);
  const [sender, setSender] = useState('');
  const [receiver, setReceiver] = useState('');
  const [messages, setMessages] = useState([]);
  const [messageFilter, setMessageFilter] = useState({ page: 1, limit: 10, searchTerm: "" });
  const [messagePagination, setMessagePagination] = useState({});
  const [groupId, setGroupId] = useState("");
  // const [searchTerm, setSearchTerm] = useState("");
  const [searchIntervalId, setSearchIntervalId] = useState(null);
  const [groups, setGroups] = useState([]);

  // Fetch all users on mount
  const getAllUsers = async () => {
    try {
      let result = await getUser(login.token);
      setUsers(result.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const getAllGroups = async () => {
    try {
      let result = await getGroups(login.token, { limit: 10, page: 1 });
      setGroups(result.data.map((x) => ({ ...x, isGroup: true })));
    } catch (error) {
      console.error("Error fetching groups:", error);
    }
  };

  useEffect(() => {
    getAllUsers();
    getAllGroups()
  }, []);

  // Fetch chat between selected users
  const fetchChat = async (fil = messageFilter) => {
    if (sender && receiver && sender !== receiver) {
      const res = await getUserChat(login.token, sender, receiver, fil)
      if (fil.page == 1) {
        setMessages([...res.data]);
        setMessagePagination(res.pagination)
      } else {
        setMessages((prev) => [...prev, ...res.data]);
        setMessagePagination(res.pagination)
      }
    } else if (groupId) {
      const res = await getGroupChat(login.token, groupId, fil)
      if (fil.page == 1) {
        setMessages([...res.data]);
        setMessagePagination(res.pagination)
      } else {
        setMessages((prev) => [...prev, ...res.data]);
        setMessagePagination(res.pagination)
      }
    }
  };

  const chatContainerRef = useRef(null);

  const handleScroll = async () => {
    const container = chatContainerRef.current;

    if (!container) return;

    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 50;

    if (isNearBottom) {
      try {
        if (messagePagination.totalPages > messageFilter.page) {
          const nextPage = messageFilter.page + 1;
          setMessageFilter({ ...messageFilter, page: nextPage });
          await fetchChat({ ...messageFilter, page: nextPage });
        }
      } catch (error) {
        console.error("Failed to load more messages:", error);
      }
    }
  };

  function getEmojiFontSize(text) {
    if (!text) return "inherit";

    const emojiRegex = /(\p{Emoji_Presentation}|\p{Extended_Pictographic})/gu;
    const emojis = [...text.matchAll(emojiRegex)].map(m => m[0]);
    const allChars = [...text.trim()];

    if (emojis.length === 1 && allChars.length === 1) return "40px";
    if (emojis.length === allChars.length) return "36px";

    return "inherit";
  }

  useEffect(() => {
    // Clear any ongoing timeout
    let timeoutId;

    if (messageFilter.searchTerm.trim() !== "") {
      timeoutId = setTimeout(() => {
        setMessageFilter((prev) => ({ ...prev, page: 1, limit: 10 }));
        fetchChat({ ...messageFilter, page: 1, limit: 10 });
      }, 500); // 500ms debounce
    } else {
      // Immediate fetch if search term is cleared
      setMessageFilter((prev) => ({ ...prev, page: 1, limit: 10 }));
      fetchChat({ ...messageFilter, page: 1, limit: 10 });
    }

    // Cleanup function: clear timeout if user types again before 500ms
    return () => {
      clearTimeout(timeoutId);
    };
  }, [messageFilter.searchTerm]);



  return (
    <div className="details-table mt-3">
      <div className="row mb-3">
        <div className="col-md-2 d-flex flex-column gap-1">
          <label className="form-label">User 1</label>
          <SearchableDropdown
            options={users} // [{ _id, name }]
            selected={sender}
            onChange={(val) => {
              setSender(val);
              setGroupId("");
            }}
            placeholder="Select User 1"
            disabled={!!groupId}
          />
        </div>

        <div className="col-md-2 d-flex flex-column gap-1">
          <label className="form-label">User 2</label>
          <SearchableDropdown
            options={users} // [{ _id, name }]
            selected={receiver}
            onChange={(val) => {
              setReceiver(val);
              setGroupId("");
            }}
            placeholder="Select User 2"
            disabled={!!groupId}
          />
        </div>

        <div className="col-md-2 d-flex flex-column gap-1">
          <label className="form-label">Group</label>
          <SearchableDropdown
            options={groups} // [{ _id, name }]
            selected={groupId}
            onChange={(val) => {
              setGroupId(val);
              setSender("");
              setReceiver("");
            }}
            placeholder="Select Group"
            disabled={!!sender || !!receiver}
          />
        </div>



        <div className="col-md-2">
          <input
            type="text"
            className="form-control"
            placeholder="Search in chat..."
            value={messageFilter.searchTerm}
            onChange={(e) => setMessageFilter({ ...messageFilter, searchTerm: e.target.value })}
          />
        </div>
        <div className="col-md-2">
          <button
            className="btn btn-primary w-100"
            onClick={() => {fetchChat({...messageFilter, page: 1})}}
            disabled={
              (!sender || !receiver || sender === receiver) && !groupId
            }
          >
            View Chat
          </button>
        </div>
      </div>
      <div
        className="border rounded p-3"
        style={{ height: '69vh', overflowY: 'auto' }}
        ref={chatContainerRef}
        onScroll={handleScroll}
      >
        {messages.length === 0 ? (
          <p className="text-muted">No chat found</p>
        ) : (
          messages.map((msg, i) => {
            let sentDate = groupId ? msg.status[0]?.sentAt : msg?.status?.sentAt
            let prevSentDate = groupId ? messages[i - 1]?.status[0]?.sentAt : messages[i - 1]?.status?.sentAt

            const currentMessageDate = new Date(sentDate).toLocaleDateString("en-GB");
            const showDate = i === 0 || currentMessageDate !== new Date(prevSentDate).toLocaleDateString("en-GB");
            return (
              <>
                {showDate && <div className="chat-date">
                  <span>{currentMessageDate}</span>
                </div>}
                <div
                  key={msg._id}
                  className={
                    "chat-bubble-container " +
                    (String(msg.sender._id) != String(sender) ? "rightsideChat" : "leftsideChat")
                  }
                >
                  <div className="chat-bubble position-relative">
                    <div className="message-content">
                      {groupId && (
                        <p
                          style={{
                            wordBreak: "break-word",
                            whiteSpace: "pre-wrap",
                            borderBottom: "1px solid #8EC73C",
                          }}
                        >
                          {msg.sender?.name}
                        </p>
                      )}
                    </div>
                    {msg.repliedMessage && (
                      <div className="replied-message mb-2 p-2 border-start border-success border-3 bg-light rounded">
                        <div className="fw-bold text-success small">
                          {msg?.repliedMessage?.sender?.name}
                        </div>
                        <div className="small text-muted"
                        dangerouslySetInnerHTML={{ __html: msg.repliedMessage.message }}
                        >
                          {/* {msg.repliedMessage.message} */}
                        </div>
                      </div>
                    )}
                    <div className="message-content" style={msg.isDeleted ? { fontStyle: "italic", fontSize: "14px" } : {}}>
                      {(msg.type === "text" || msg.isDeleted) ? (
                        <p style={{ wordBreak: "break-word", whiteSpace: "pre-wrap", fontSize: getEmojiFontSize(msg.message) }}
                        dangerouslySetInnerHTML={{ __html: msg.message }}
                        >
                          {/* {msg.message} */}
                        </p>
                      ) : (
                        <FileMessage file={msg} />
                      )}
                    </div>

                    <div className="time-read">
                      {msg.type === "text" && (
                        <button
                          className="copy-btn"
                          onClick={() => {
                            copyToClipboard(msg.message);
                          }}
                        >
                          <i className="fa fa-copy" style={{ fontSize: "14px" }}></i>
                        </button>
                      )}
                      <div className="timestamp">{moment(msg?.createdAt).format("h:mm a")}</div>
                    </div>
                  </div>
                </div>
              </>
            )
          })
        )}
      </div>
    </div>
  );
};
